import { useEffect, useMemo } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  Theme,
} from "@mui/material/styles";
import { DEFAULT_COMPANY_CONFIG } from "@/const";
import merge from "lodash/merge";
import "@fontsource/hanken-grotesk/300.css";
import "@fontsource/hanken-grotesk/400.css";
import "@fontsource/hanken-grotesk/500.css";
import "@fontsource/hanken-grotesk/700.css";
import { useCompanyConfigContext } from "@/context/CompanyConfigContext";
import { useLocation } from "react-router-dom";

interface DynamicTitleProps {
  title: string;
}

const defaultTheme = {
  palette: {
    primary: {
      main: "#5C9CD0",
    },
    secondary: {
      main: "#14802F",
    },
  },
  typography: {
    fontFamily: "Hanken Grotesk, Arial",
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (defaultTheme: Theme) => ({
        a: {
          color: defaultTheme.palette.primary.main,
        },
      }),
    },
  },
};

const DynamicTitle: React.FC<DynamicTitleProps> = ({ title }) => {
  const location = useLocation();

  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const routeTitle =
      pathSegments.length > 0
        ? `${title} - ${pathSegments.map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1)).join(" - ")}`
        : title;
    document.title = routeTitle;
  }, [title, location.pathname]);

  return null;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { companyConfig, company } = useCompanyConfigContext();

  const theme = useMemo(() => {
    // Get theme from company's settingsByConfig using the current companyConfig.id
    const configTheme =
      companyConfig?.id && company?.settingsByConfig?.[companyConfig.id]?.theme;

    console.log("configTheme", configTheme);

    return createTheme(
      merge(
        {},
        defaultTheme,
        configTheme || companyConfig?.settings?.theme || {},
      ),
    );
  }, [
    companyConfig?.settings?.theme,
    companyConfig?.id,
    company?.settingsByConfig,
  ]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <DynamicTitle
        title={
          companyConfig?.settings?.strings?.rewardsName ||
          DEFAULT_COMPANY_CONFIG.REWARDS_NAME
        }
      />
      {children}
    </MuiThemeProvider>
  );
};
